<script setup>
import { computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();

const pending = useMapGetter('painelHumanAttention/getPendingRequests');
const attended = useMapGetter('painelHumanAttention/getAttendedRequests');
const uiFlags = useMapGetter('painelHumanAttention/getUiFlags');

const accountId = computed(() => route.params.accountId);

const formatPhone = phone => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  const local =
    digits.startsWith('55') && digits.length > 11 ? digits.slice(2) : digits;
  if (local.length === 11)
    return `(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`;
  if (local.length === 10)
    return `(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`;
  return phone;
};

const initials = name => {
  if (!name) return '?';
  return name
    .split(' ')
    .slice(0, 2)
    .map(part => part.charAt(0).toUpperCase())
    .join('');
};

const openContact = req => {
  if (!req.contact?.id) return;
  router.push(`/app/accounts/${accountId.value}/contacts/${req.contact.id}`);
};

const timeAgo = iso => {
  if (!iso) return '';
  try {
    return formatDistanceToNow(new Date(iso), {
      addSuffix: true,
      locale: ptBR,
    });
  } catch {
    return '';
  }
};

const attendRequest = req => {
  store.dispatch('painelHumanAttention/startAttendance', req);
  if (req.conversation?.display_id) {
    router.push({
      name: 'inbox_conversation',
      params: { conversation_id: req.conversation.display_id },
    });
  }
};

const openConversation = req => {
  if (req.conversation?.display_id) {
    router.push({
      name: 'inbox_conversation',
      params: { conversation_id: req.conversation.display_id },
    });
  }
};

let pollInterval = null;

onMounted(() => {
  store.dispatch('painelHumanAttention/fetchRequests');
  pollInterval = setInterval(
    () => store.dispatch('painelHumanAttention/fetchRequests'),
    30000
  );
});

onUnmounted(() => {
  clearInterval(pollInterval);
});
</script>

<template>
  <div
    class="flex h-full w-full min-w-0 flex-1 flex-col overflow-hidden bg-n-surface-1"
  >
    <div class="flex-1 overflow-y-auto">
      <div class="flex flex-col gap-6 p-6">
        <header class="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 class="text-xl font-semibold text-n-slate-12">
              {{ t('PAINEL.HUMAN_ATTENTION.PAGE.TITLE') }}
            </h1>
            <p class="mt-1 text-sm text-n-slate-11">
              {{ t('PAINEL.HUMAN_ATTENTION.PAGE.SUBTITLE') }}
            </p>
          </div>
        </header>

        <!-- Pending -->
        <section>
          <div class="mb-3 flex items-center gap-2">
            <span
              class="text-xs font-semibold uppercase tracking-wide text-n-slate-12"
            >
              {{ t('PAINEL.HUMAN_ATTENTION.PAGE.PENDING') }}
            </span>
            <span
              class="rounded-full bg-n-ruby-3 px-2 py-0.5 text-[11px] font-bold text-n-ruby-11"
            >
              {{ pending.length }}
            </span>
          </div>

          <p
            v-if="!pending.length && !uiFlags.isFetching"
            class="px-1 text-sm text-n-slate-11"
          >
            {{ t('PAINEL.HUMAN_ATTENTION.PAGE.NO_PENDING') }}
          </p>

          <div
            v-for="req in pending"
            :key="req.id"
            class="mb-2 flex items-center justify-between rounded-xl border border-n-ruby-7 bg-n-solid-2 px-4 py-3.5"
          >
            <div class="flex min-w-0 items-center gap-3">
              <button
                type="button"
                class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-n-ruby-3 text-xs font-semibold text-n-ruby-11"
                :class="
                  req.contact?.id
                    ? 'cursor-pointer hover:opacity-80'
                    : 'cursor-default'
                "
                @click="openContact(req)"
              >
                {{ initials(req.contact?.name || req.phone_number) }}
              </button>
              <div class="min-w-0">
                <button
                  type="button"
                  class="truncate text-sm font-medium text-n-slate-12"
                  :class="
                    req.contact?.id
                      ? 'cursor-pointer hover:underline'
                      : 'cursor-default'
                  "
                  @click="openContact(req)"
                >
                  {{ req.contact?.name || formatPhone(req.phone_number) }}
                </button>
                <div
                  class="mt-0.5 flex items-center gap-1.5 truncate text-xs text-n-slate-11"
                >
                  <span>{{ formatPhone(req.phone_number) }}</span>
                  <span class="text-n-slate-9">·</span>
                  <span>{{ req.conversation?.inbox_name || '—' }}</span>
                  <span class="text-n-slate-9">·</span>
                  <span>{{ timeAgo(req.created_at) }}</span>
                </div>
              </div>
            </div>
            <button
              type="button"
              :disabled="!!uiFlags.isAttending[req.id]"
              class="rounded-md bg-n-ruby-9 px-3.5 py-2 text-xs font-semibold text-white transition hover:bg-n-ruby-10 disabled:opacity-50"
              @click="attendRequest(req)"
            >
              {{ t('PAINEL.HUMAN_ATTENTION.PAGE.ATTEND') }}
            </button>
          </div>
        </section>

        <!-- Attended -->
        <section>
          <div class="mb-3 flex items-center gap-2">
            <span
              class="text-xs font-semibold uppercase tracking-wide text-n-slate-11"
            >
              {{ t('PAINEL.HUMAN_ATTENTION.PAGE.ATTENDED') }}
            </span>
            <span
              class="rounded-full bg-n-solid-3 px-2 py-0.5 text-[11px] font-bold text-n-slate-11"
            >
              {{ attended.length }}
            </span>
          </div>

          <p
            v-if="!attended.length && !uiFlags.isFetching"
            class="px-1 text-sm text-n-slate-11"
          >
            {{ t('PAINEL.HUMAN_ATTENTION.PAGE.NO_ATTENDED') }}
          </p>

          <div
            v-for="req in attended"
            :key="req.id"
            class="mb-1.5 flex items-center justify-between rounded-xl border border-n-weak bg-n-solid-2 px-4 py-3 opacity-80"
          >
            <div class="flex min-w-0 items-center gap-3">
              <div
                class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-n-solid-3 text-[11px] font-semibold text-n-slate-11"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  class="h-3.5 w-3.5"
                >
                  <path
                    fill-rule="evenodd"
                    d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
              <div class="min-w-0">
                <div class="truncate text-sm font-medium text-n-slate-11">
                  {{ req.contact?.name || formatPhone(req.phone_number) }}
                </div>
                <div class="mt-0.5 text-[11px] text-n-slate-10">
                  {{
                    t('PAINEL.HUMAN_ATTENTION.PAGE.ATTENDED_BY', {
                      name: req.attended_by?.name || '—',
                    })
                  }}
                </div>
              </div>
            </div>
            <button
              type="button"
              class="px-3 py-1.5 text-xs font-medium text-n-slate-11 hover:text-n-slate-12"
              @click="openConversation(req)"
            >
              {{ t('PAINEL.HUMAN_ATTENTION.PAGE.OPEN_CONVERSATION') }}
            </button>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>
